import {
  type CSSProperties,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Map as MapGL, type MapRef, NavigationControl } from "react-map-gl/mapbox";

import { DeckGLOverlay } from "@/components/deckgl/deckgl-overlay";
import useMediaQuery from "@/features/shared/hooks/use-media-query";
import { accountOwnedMapStyleUrl, knownMapboxStyles } from "@/lib/mapbox/base-layer-styles";
import useMapViewState from "@/features/shared/widgets/map/hooks/useMapViewState";

import { MAP_WHEEL_ZOOM_CHROME, pointerArmsMapWheelZoom } from "./map-wheel-zoom";
import { highestRiskPictureItemCode } from "./highest-risk-picture-level";
import {
  filterRiskPictureMapItems,
  resetRiskPicturePhenomenaFilter,
  type RiskPictureMapFilter,
} from "./risk-picture-map-filter";
import { RiskPictureMapFilters } from "./risk-picture-map-filters";
import {
  chanceStripeWorldScale,
  riskPictureItemPosition,
  riskPictureLayers,
} from "./risk-picture-layers";
import { RiskPictureMarkerCluster } from "./risk-picture-marker-cluster";
import { groupOverlappingRiskPictureMarkers } from "./risk-picture-marker-groups";
import type { RiskPictureDay } from "./risk-picture-model";
import { useFadingRiskPicturePolygons } from "./risk-picture-polygon-fade";
import {
  riskPictureDrawnItems,
  riskPictureMarkerItems,
  riskPicturePreviewItems,
  type RiskPictureSelection,
} from "./risk-picture-selection";
import { RiskPictureSidebar } from "./risk-picture-sidebar";
import {
  buildRiskPictureTimeline,
  riskPictureDrawerMaxHeight,
  riskPictureTimelineChartHeight,
} from "./risk-picture-timeline";
import { RiskPictureTimelineSidebar } from "./risk-picture-timeline-sidebar";
import styles from "./risk-picture.module.css";

import "mapbox-gl/dist/mapbox-gl.css";

const NL_BOUNDS = [
  { lat: 53.67885, lng: 3.273652 },
  { lat: 50.542863, lng: 7.279572 },
];

/** Same as `--upto-md` in `@/styles/breakpoints.css`. */
const UPTO_MD_MEDIA = "(max-width: 800px)";

interface RiskPictureMapProps {
  days: RiskPictureDay[];
  phenomena: readonly string[];
  dayIndex: number;
  /** Day currently painted on the map; can differ while a sidebar row is hovered. */
  mapDayIndex: number;
  /** One warning picked in the sidebar or on the map. Only its polygon is drawn. */
  selectedItemId?: string;
  previewItemId?: string;
  onHoverItem: (item: RiskPictureSelection | undefined) => void;
  onSelectDay: (index: number) => void;
  onSelectItem: (dayIndex: number, itemId: string) => void;
  onBack: () => void;
  /** Only resizes the canvas; the toggle lives in the map card's toolbar. */
  fullscreen: boolean;
}

export const RiskPictureMap = ({
  days,
  phenomena,
  dayIndex,
  mapDayIndex,
  selectedItemId,
  previewItemId,
  onHoverItem,
  onSelectDay,
  onSelectItem,
  onBack,
  fullscreen,
}: RiskPictureMapProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<MapRef>(null);
  const releaseWheelZoom = useRef<(() => void) | null>(null);
  const [wheelZoom, setWheelZoom] = useState(false);
  const [viewEpoch, setViewEpoch] = useState(0);
  const [hoveredMarkerId, setHoveredMarkerId] = useState<string>();
  const [openClusterId, setOpenClusterId] = useState<string>();
  const [calendarOpen, setCalendarOpen] = useState(false);
  // Fullscreen drawer only. Every entry starts closed, so the map gets the whole card first.
  const [timelineDrawerOpen, setTimelineDrawerOpen] = useState(false);
  const [timelineDrawerHeight, setTimelineDrawerHeight] = useState(0);
  // The open drawer is capped against this, so it cannot cover the whole map on a short phone.
  const [mapHeight, setMapHeight] = useState(0);
  useLayoutEffect(() => {
    const map = containerRef.current;
    if (!map) return;
    const measure = () => {
      setMapHeight(map.offsetHeight);
      // Mapbox only tracks window resize. The timeline band under the map changes this box's
      // height on day switches; without this the old canvas leaves a white strip above it.
      mapRef.current?.resize();
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(map);
    return () => observer.disconnect();
  }, []);
  // Mirrors `--upto-md`. The day buttons sit above the scroll area there, so reserving the
  // fullest day's height only adds empty scrolling on quiet days.
  const isMobile = useMediaQuery(UPTO_MD_MEDIA);
  useLayoutEffect(() => {
    if (fullscreen) setTimelineDrawerOpen(false);
  }, [fullscreen]);
  const [filter, setFilter] = useState<RiskPictureMapFilter>({
    showIssued: true,
    showChance: true,
    hiddenPhenomena: new Set(),
  });
  // Reset in the same render as a selected-day change, not on sidebar hover previews.
  const [filterDayIndex, setFilterDayIndex] = useState(dayIndex);
  if (filterDayIndex !== dayIndex) {
    setFilterDayIndex(dayIndex);
    setFilter(resetRiskPicturePhenomenaFilter);
  }
  // The day strip and timeline always show the full warning picture, independent of the map.
  const timelineStrip = useMemo(
    () =>
      days.map((day) => ({
        date: day.date,
        ...highestRiskPictureItemCode(day.items),
      })),
    [days],
  );
  const items = days[mapDayIndex]?.items ?? [];
  const selectedItem = selectedItemId
    ? items.find((item) => item.id === selectedItemId)
    : undefined;
  const filteredItems = useMemo(
    () => filterRiskPictureMapItems(items, filter),
    [items, filter],
  );
  // Tallest unfiltered day. The open band keeps this height so the next-day
  // button stays under the cursor when a quieter day would otherwise shrink it.
  const timelineChartMinHeight = useMemo(
    () =>
      days.reduce((maxHeight, day) => {
        const rows = buildRiskPictureTimeline(day.items, day.date, { phenomena }).rows;
        return Math.max(maxHeight, riskPictureTimelineChartHeight(rows));
      }, 0),
    [days, phenomena],
  );
  const markerItems = useMemo(
    () => riskPictureMarkerItems(items, filteredItems, selectedItem?.id, previewItemId),
    [items, filteredItems, selectedItem, previewItemId],
  );
  // A card can disappear when changing days or filters without firing mouseleave.
  const focusedItemId = markerItems.some((item) => item.id === hoveredMarkerId)
    ? hoveredMarkerId
    : previewItemId;
  // An explicit pick and a distinct hover preview remain drawn despite filters.
  const visibleItems = useMemo(() => {
    const drawn = selectedItem
      ? riskPictureDrawnItems(items, selectedItem.id, focusedItemId)
      : riskPicturePreviewItems(items, filteredItems, focusedItemId);
    // Hover focus shows only the focused warning; the others are removed from the map.
    const focused = drawn.find((item) => item.id === focusedItemId);
    return focused ? [focused] : drawn;
  }, [items, filteredItems, selectedItem, focusedItemId]);
  const viewStateOptions = useMemo(
    () => ({ containerRef, initialCoords: NL_BOUNDS }),
    [containerRef.current],
  );
  const { mapViewState } = useMapViewState(viewStateOptions);

  useEffect(() => () => releaseWheelZoom.current?.(), []);

  // Mapbox only resizes on window resize. Resize in the same commit as the fullscreen flip,
  // so the view transition's new snapshot already has the right canvas size.
  // Entering keeps center and zoom, so a zoomed-in view stays zoomed in. Leaving fits NL
  // again: the smaller card would otherwise crop the country around the fullscreen center.
  const wasFullscreen = useRef(fullscreen);
  useLayoutEffect(() => {
    const map = mapRef.current;
    map?.resize();
    const leaving = wasFullscreen.current && !fullscreen;
    wasFullscreen.current = fullscreen;
    if (!leaving) return;
    map?.fitBounds(
      [
        [NL_BOUNDS[0].lng, NL_BOUNDS[0].lat],
        [NL_BOUNDS[1].lng, NL_BOUNDS[1].lat],
      ],
      { duration: 0 },
    );
  }, [fullscreen]);

  const setMapWheelZoom = useCallback((enabled: boolean) => {
    const scrollZoom = mapRef.current?.getMap()?.scrollZoom;
    if (!scrollZoom) return;
    if (enabled) scrollZoom.enable();
    else scrollZoom.disable();
  }, []);

  // Arm during pointerdown, before pointerup, so a quick click cannot leave zoom stuck on.
  // Enable Mapbox here too: the wheel can arrive before the scrollZoom prop re-renders.
  // Fullscreen locks body scroll, so the wheel always zooms there and nothing needs arming.
  const armWheelZoom = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      if (fullscreen) return;
      const node = event.target instanceof Node ? event.target : null;
      const element = node instanceof Element ? node : (node?.parentElement ?? null);
      const onChrome = Boolean(element?.closest(MAP_WHEEL_ZOOM_CHROME));
      if (!pointerArmsMapWheelZoom(event.button, onChrome)) return;

      releaseWheelZoom.current?.();
      const release = () => {
        releaseWheelZoom.current = null;
        setMapWheelZoom(false);
        setWheelZoom(false);
        window.removeEventListener("pointerup", release);
        window.removeEventListener("pointercancel", release);
        window.removeEventListener("blur", release);
      };
      releaseWheelZoom.current = release;
      window.addEventListener("pointerup", release);
      window.addEventListener("pointercancel", release);
      window.addEventListener("blur", release);
      setMapWheelZoom(true);
      setWheelZoom(true);
    },
    [fullscreen, setMapWheelZoom],
  );

  // A map pick shows the warning's details on top of the panel. It leaves the calendar alone,
  // so closing the details returns to whatever the calendar was: open or collapsed.
  const selectFromMap = useCallback(
    (itemId: string) => {
      // The picked marker unmounts without necessarily firing mouseleave.
      setHoveredMarkerId(undefined);
      onSelectItem(mapDayIndex, itemId);
    },
    [mapDayIndex, onSelectItem],
  );

  // viewEpoch bumps on load and zoom. Panning does not change icon spacing.
  const markerGroups = useMemo(() => {
    const map = mapRef.current?.getMap();
    if (viewEpoch === 0 || !map?.loaded()) {
      return markerItems.map((item) => ({ id: item.id, items: [item] }));
    }

    return groupOverlappingRiskPictureMarkers(markerItems, (item) => {
      const [longitude, latitude] = riskPictureItemPosition(item);
      const point = map.project([longitude, latitude]);
      return { x: point.x, y: point.y };
    });
  }, [markerItems, viewEpoch]);

  // Sidebar/timeline picks and returning to the day overview also invalidate map hover.
  useEffect(() => {
    setHoveredMarkerId(undefined);
    setOpenClusterId(undefined);
  }, [mapDayIndex, selectedItemId]);

  const polygons = useFadingRiskPicturePolygons(visibleItems);
  const stripeScale = chanceStripeWorldScale(mapViewState?.zoom);
  const layers = useMemo(
    () =>
      riskPictureLayers(
        polygons.map((polygon) => polygon.item),
        "day",
        (item) => selectFromMap(item.id),
        mapViewState?.zoom,
        false,
        new Map(
          polygons.map((polygon) => [
            polygon.item.id,
            { opacity: polygon.opacity, leaving: polygon.leaving },
          ]),
        ),
        focusedItemId,
      ),
    [polygons, selectFromMap, mapViewState?.zoom, stripeScale, focusedItemId],
  );

  return (
    <div className={styles.mapStage}>
      <div
        ref={containerRef}
        className={styles.map}
        // The drawer opens over the map's bottom edge; lifts the zoom buttons above it.
        style={{ "--timeline-drawer-height": `${timelineDrawerHeight}px` } as CSSProperties}
        onPointerDownCapture={armWheelZoom}
      >
        {mapViewState && (
          <MapGL
            ref={mapRef}
            initialViewState={mapViewState}
            onLoad={() => setViewEpoch((epoch) => epoch + 1)}
            onZoom={() => setViewEpoch((epoch) => epoch + 1)}
            style={{ position: "absolute", inset: 0 }}
            attributionControl={false}
            projection={{ name: "mercator" }}
            mapStyle={accountOwnedMapStyleUrl(knownMapboxStyles.MonochromeLight)}
            mapboxAccessToken={import.meta.env.VITE_MAP_ACCESS_TOKEN}
            maxPitch={0}
            bearingSnap={360}
            bearing={0}
            scrollZoom={fullscreen || wheelZoom}
          >
            {/* Deck stays off so it does not take the wheel. Mapbox zooms, and only while the button is held. */}
            <DeckGLOverlay interleaved controller={{ scrollZoom: false }} layers={layers} />
            <NavigationControl showCompass={false} position="bottom-right" showZoom />
            {/* Hover focus hides every other marker and stack, not just dims them. */}
            {(focusedItemId
              ? markerGroups.filter((group) =>
                  group.items.some((item) => item.id === focusedItemId),
                )
              : markerGroups
            ).map((group) => (
              <RiskPictureMarkerCluster
                key={group.id}
                items={group.items}
                previewItemId={previewItemId}
                focusedItemId={focusedItemId ?? selectedItem?.id}
                selectedItemId={selectedItem?.id}
                open={openClusterId === group.id}
                onOpenChange={(open) => setOpenClusterId(open ? group.id : undefined)}
                onSelect={selectFromMap}
                onHoverItem={setHoveredMarkerId}
              />
            ))}
          </MapGL>
        )}
        <div className={styles.mapChrome} data-map-chrome="">
          <RiskPictureMapFilters
            items={items}
            phenomena={phenomena}
            filter={filter}
            onChange={setFilter}
          />
          <RiskPictureSidebar
            days={days}
            dayIndex={dayIndex}
            selectedItem={selectedItem}
            calendarOpen={calendarOpen}
            onCalendarOpenChange={setCalendarOpen}
            // Collapsed, the calendar is off-screen: highlighting or scrolling it is wasted work.
            highlighted={
              calendarOpen && focusedItemId
                ? { dayIndex: mapDayIndex, itemId: focusedItemId }
                : undefined
            }
            onSelectDay={onSelectDay}
            onSelectItem={onSelectItem}
            onHoverItem={onHoverItem}
            onBack={onBack}
          />
        </div>
      </div>
      <RiskPictureTimelineSidebar
        day={days[dayIndex]}
        strip={timelineStrip}
        dayIndex={dayIndex}
        phenomena={phenomena}
        items={days[dayIndex]?.items ?? []}
        // The fullscreen drawer opens over the map, so a quieter day shrinking it moves
        // nothing; it only needs the current day's height.
        chartMinHeight={isMobile || fullscreen ? 0 : timelineChartMinHeight}
        selectedItemId={selectedItem?.id}
        // The timeline shows `dayIndex`; a sidebar hover can put the map on another day.
        highlightedItemId={mapDayIndex === dayIndex ? focusedItemId : undefined}
        drawer={fullscreen}
        drawerMaxHeight={riskPictureDrawerMaxHeight(mapHeight)}
        open={timelineDrawerOpen}
        onOpenChange={setTimelineDrawerOpen}
        onDrawerHeightChange={setTimelineDrawerHeight}
        onSelectDay={onSelectDay}
        onSelectItem={selectFromMap}
        onDeselectItem={onBack}
        onHoverItem={onHoverItem}
      />
    </div>
  );
};
